import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./styles/AdminDashboard.css";

// 🔒 Only allow this page to render if it was opened via the Admin button
// on the Information.aspx page (which sets this sessionStorage flag right
// before calling window.open). A brand-new tab / pasted URL / bookmark
// won't have this flag set, since sessionStorage is only copied over to
// tabs opened programmatically (via window.open) from an already-logged-in
// same-origin tab — it is NOT shared with a freshly typed/bookmarked URL.
const ADMIN_ACCESS_KEY = "wf_admin_access";
const LOGIN_URL = "https://portfolio.wealth-firstonline.com/wf/";

// Converts a JS Date object into the "yyyy-mm-dd" string the backend API expects.
// The display format shown to the user (dd/MMMM/yyyy) is handled separately by
// react-datepicker's `dateFormat` prop — this only affects what we send to the API.
const toApiDateString = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Formats the table's "Date" column the same way as the calendar pickers (17/July/2026).
// Falls back to a dash if the value is missing/invalid instead of throwing.
const formatTableDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "-";
  return format(d, "dd/MMMM/yyyy");
};

function AdminDashboard() {
  const todayObj = new Date();
  const today = toApiDateString(todayObj); // kept as yyyy-mm-dd string, used for the "did user change it" check
  const [fromDate, setFromDate] = useState(todayObj); // now a Date object → first calendar → default today
  const [toDate, setToDate] = useState(todayObj);
  const [name, setName] = useState("");
  const [pan, setPan] = useState("");
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);

  const [selectedClient, setSelectedClient] = useState(null); // client clicked
  const [stageName, setStageName] = useState("");             // stage clicked
  const [stageFields, setStageFields] = useState({});         // field-level status

  // Checked once, synchronously, so the dashboard never flashes on screen
  // before we know whether access is allowed.
  const [isAuthorized] = useState(
    () => sessionStorage.getItem(ADMIN_ACCESS_KEY) === "true"
  );

  // 🔒 If someone opens/pastes/bookmarks this URL directly (no flag set),
  // kick them straight back to the login page instead of showing data.
  useEffect(() => {
    if (!isAuthorized) {
      window.location.replace(LOGIN_URL);
    }
  }, [isAuthorized]);

  useEffect(() => {
    if (isAuthorized) {
      searchClients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized]);

  // 🔹 Refresh table if returning from onboarding page
useEffect(() => {
  if (!isAuthorized) return;

  const urlParams = new URLSearchParams(window.location.search);
  const pan = urlParams.get("pan");
  const admin = urlParams.get("admin");

  if (pan && admin === "true") {
    setTimeout(() => {
      searchClients();
    }, 500);

    window.history.replaceState({}, document.title, "/wfmf/admin");
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isAuthorized]);
  // Fetch client list
  const searchClients = async () => {
  try {
    const params = {};

    // Only send fromDate if user changed it
    if (toApiDateString(fromDate) !== today) params.fromDate = toApiDateString(fromDate); // ← key fix
    if (toDate) params.toDate = toApiDateString(toDate);
    if (name.trim() !== "") params.name = name.trim();
    if (pan.trim() !== "") params.pan = pan.trim();

    const res = await axios.get("/api/admin/clients", { params });

    setClients(res.data.clients);
    setTotal(res.data.totalClients);
  } catch (error) {
    console.error(error);
  }
};
  // Handle click on stage
  const handleStageClick = async (client, stage) => {

  // 🔥 If stage is pending → open onboarding page
  const stageStatus = {
    STAGE1: client.Registration,
    STAGE2: client.GeneralInfo,
    STAGE3: client.AddressDetails,
    STAGE4: client.BankDetails,
    STAGE5: client.NomineeDetails
  };

  if (stageStatus[stage] !== "DONE") {

    const stepMap = {
  STAGE1: 2, // Name page
  STAGE2: 4, // General
  STAGE3: 5, // Address
  STAGE4: 6, // Bank
  STAGE5: 7  // Nominee
};

    const step = stepMap[stage];

   window.location.href =
  `/wfmf/onboarding?pan=${client.PAN}&step=${step}&admin=true`;

    return;
  }

  try {

    // 🔥 fetch full client data again
    const res = await axios.get("/api/admin/clients", {
  params: { pan: client.PAN }
});

    const fullClient = res.data.clients[0];

// 1️⃣ Update the panel
setSelectedClient(fullClient);
setStageName(stage);

// 2️⃣ Update the table so previous stages stay Done and current stage updates
setClients(prevClients =>
  prevClients.map(c =>
    c.PAN === fullClient.PAN ? fullClient : c
  )
);

    let fields = {};

    if (stage === "STAGE1") {
      fields = {
        pan: fullClient.PAN,
        full_name: fullClient.FULL_NAME,
        email: fullClient.EMAIL,
        mobile: fullClient.MOBILE
      };
    }

    else if (stage === "STAGE2") {
      fields = {
        date_of_birth: fullClient.date_of_birth,
        gender: fullClient.gender,
        occupation: fullClient.occupation,
        marital_status: fullClient.marital_status
      };
    }

    else if (stage === "STAGE3") {
      fields = {
        address_line: fullClient.address_line,
        street: fullClient.street,
        area: fullClient.area,
        city: fullClient.city,
        state: fullClient.state,
        pincode: fullClient.pincode
      };
    }

    else if (stage === "STAGE4") {
      fields = {
        bank_name: fullClient.bank_name,
        branch_name: fullClient.branch_name,
        account_type: fullClient.account_type,
        account_number: fullClient.account_number,
        ifsc: fullClient.ifsc,
        micr: fullClient.micr
      };
    }

    else if (stage === "STAGE5") {
      fields = {
        nominee_name: fullClient.nominee_name,
        nominee_relation: fullClient.nominee_relation,
        nominee_id_type: fullClient.nominee_id_type,
        nominee_id_number: fullClient.nominee_id_number,
        nominee_email: fullClient.nominee_email,
        nominee_contact: fullClient.nominee_contact,
        nominee_is_minor: fullClient.nominee_is_minor,
        nominee_dob: fullClient.nominee_dob,
        guardian_name: fullClient.guardian_name,
        nominee_address: fullClient.nominee_address,
        nominee_street: fullClient.nominee_street,
        nominee_area: fullClient.nominee_area,
        nominee_city: fullClient.nominee_city,
        nominee_state: fullClient.nominee_state,
        nominee_pincode: fullClient.nominee_pincode
      };
    }

    setStageFields(fields);

  } catch (err) {
    console.error(err);
  }

};
  const closePanel = () => {
    setSelectedClient(null);
    setStageName("");
    setStageFields({});
  };
  const exportExcel = () => {

  const params = new URLSearchParams();

  if (toApiDateString(fromDate) !== today) params.append("fromDate", toApiDateString(fromDate));
  if (toDate) params.append("toDate", toApiDateString(toDate));
  if (name.trim() !== "") params.append("name", name.trim());
  if (pan.trim() !== "") params.append("pan", pan.trim());

  window.open(`/wfmb/api/admin/export-excel?${params.toString()}`);
};

  // Don't render any dashboard content/data while we're bouncing
  // an unauthorized visitor back to the login page.
  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="admin-container">
      <h2>Admin Dashboard</h2>

      <div className="search-box">
        <div className="search-row">
          <DatePicker
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            dateFormat="dd/MMMM/yyyy"
            className="search-input"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            todayButton="Today"
          />

          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            dateFormat="dd/MMMM/yyyy"
            className="search-input"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            todayButton="Today"
          />
          <input
            type="text"
            className="search-input"
            placeholder="Search Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            type="text"
            className="search-input"
            placeholder="Search PAN"
            value={pan}
            maxLength={10}
            onChange={(e) => {
              let value = e.target.value.toUpperCase();
              const regex = /^([A-Z]{0,5})([0-9]{0,4})([A-Z]{0,1})$/;
              if (regex.test(value)) setPan(value);
            }}
          />
        </div>

        <div style={{marginTop:"10px"}}>

      <div className="button-row">

<button className="search-btn" onClick={searchClients}>
Search
</button>

<button className="export-btn" onClick={exportExcel}>
Export Excel
</button>

</div>

</div>
      </div>

      <h3>Total Clients: {total}</h3>

      <table className="client-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>PAN</th>
            <th>Email</th>
            <th>Mobile</th>
            <th>Date</th>
            <th>Registration</th>
            <th>General</th>
            <th>Address</th>
            <th>Bank</th>
            <th>Nominee</th>
          </tr>
        </thead>
        <tbody>
          {clients.map((c, idx) => (
            <tr key={idx}>
              <td>{c.FULL_NAME}</td>
              <td>{c.PAN}</td>
              <td>{c.EMAIL}</td>
              <td>{c.MOBILE}</td>
              <td>{formatTableDate(c.CREATED_AT)}</td>

              <td
                onClick={() => handleStageClick(c, "STAGE1")}
                className={c.Registration === "DONE" ? "done" : "pending"}
              >
                {c.Registration}
              </td>

              <td
                onClick={() => handleStageClick(c, "STAGE2")}
                className={c.GeneralInfo === "DONE" ? "done" : "pending"}
              >
                {c.GeneralInfo}
              </td>

              <td
                onClick={() => handleStageClick(c, "STAGE3")}
                className={c.AddressDetails === "DONE" ? "done" : "pending"}
              >
                {c.AddressDetails}
              </td>

              <td
                onClick={() => handleStageClick(c, "STAGE4")}
                className={c.BankDetails === "DONE" ? "done" : "pending"}
              >
                {c.BankDetails}
              </td>

              <td
                onClick={() => handleStageClick(c, "STAGE5")}
                className={c.NomineeDetails === "DONE" ? "done" : "pending"}
              >
                {c.NomineeDetails}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pending Panel */}
      {selectedClient && (
        <div className="pending-details">
          <h3>Pending Details for: {selectedClient.FULL_NAME}</h3>
          <p><strong>PAN:</strong> {selectedClient.PAN}</p>
          <p><strong>Email:</strong> {selectedClient.EMAIL ? "✅ Provided" : "❌ Missing"}</p>
          <p><strong>Mobile:</strong> {selectedClient.MOBILE ? "✅ Provided" : "❌ Missing"}</p>

          {stageName && stageFields && (
            <>
              <h4>{stageName.replace("STAGE", "Stage ")} Fields:</h4>
              <ul>
{Object.keys(stageFields).map((field, idx) => {

const value = stageFields[field];

const isMissing =
  value === null ||
  value === undefined ||
  value === "" ||
  value === " " ||
  value === "NULL";

return (
<li key={idx}>
{field}: {isMissing ? "❌ Missing" : "✅ Provided"}
</li>
);

})}
</ul>
            </>
          )}

          <button className="close-btn" onClick={closePanel}>Close</button>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;